import { AppBar, Box, IconButton, Toolbar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
    useConfigurationContext,
    useThemeLogo,
} from '../../../root/ConfigurationContext';
import { NotificationBell } from '../../../notifications/NotificationBell';
import { NotificationToaster } from '../../../notifications/NotificationToaster';
import {
    MORE_SHEET_ID,
    TOP_BAR_ATTRIBUTE,
    TOP_BAR_HEIGHT_PX,
    padLeft,
    padRight,
} from '../chrome';
import { BellIcon, MenuIcon } from './icons';

interface MobileTopBarProps {
    /** Users without a tab bar reach the More sheet from here. */
    showMore: boolean;
    /** The More sheet is open (the button says so, aria-expanded). */
    moreOpen?: boolean;
    onMore: () => void;
}

export function MobileTopBar({
    showMore,
    moreOpen = false,
    onMore,
}: MobileTopBarProps) {
    const { title } = useConfigurationContext();
    const logo = useThemeLogo();
    return (
        <>
            <AppBar
                position="fixed"
                color="primary"
                elevation={1}
                // The More sheet makes the bar inert by this mark.
                {...{ [TOP_BAR_ATTRIBUTE]: '' }}
                sx={theme => ({
                    pt: 'env(safe-area-inset-top, 0px)',
                    // The mockup's soft shadow. The light theme gives every app bar
                    // the desktop header's heavier one; dark has none to replace.
                    ...(theme.palette.mode === 'light'
                        ? { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }
                        : {}),
                })}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: `${TOP_BAR_HEIGHT_PX}px !important`,
                        height: TOP_BAR_HEIGHT_PX,
                        // + the notch inset of a landscape phone
                        pl: padLeft(16),
                        pr: padRight(4),
                    }}
                >
                    <Box
                        component={Link}
                        to="/"
                        aria-label={`${title} home`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 44,
                            mr: 'auto',
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt={title}
                            sx={{ height: 15 }}
                        />
                    </Box>
                    {/* The bell is shared with the desktop header: size it here. */}
                    <Box
                        sx={{
                            '& .MuiIconButton-root': { width: 44, height: 44 },
                        }}
                    >
                        <NotificationBell
                            icon={BellIcon}
                            dot
                            sx={theme => ({
                                color: alpha(theme.palette.text.primary, 0.7),
                            })}
                        />
                    </Box>
                    {showMore && (
                        <IconButton
                            aria-label="More"
                            aria-haspopup="dialog"
                            aria-expanded={moreOpen}
                            aria-controls={MORE_SHEET_ID}
                            onClick={onMore}
                            sx={{ width: 44, height: 44 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>
            {/* Outside the bar: MUI's Snackbar is not portaled, so inside it
                the toast would be inert with the bar while the More sheet is
                open, and could not be clicked. Above the sheet (drawer) and
                the dock (drawer + 1), but below the dialogs (modal) that the
                sheet opens: the Snackbar's own 1400 would cover those. */}
            <NotificationToaster
                sx={theme => ({ zIndex: theme.zIndex.drawer + 2 })}
            />
        </>
    );
}
